/**
 * Commodity Expiry Upload Page (KAN-259)
 *
 * Admin-only page for importing trade expiry / settlement data.
 *
 * Flow:
 *   1. Select an expiry import config
 *   2. Upload a CSV / Excel file
 *   3. Preview — rows show match status (matched / not_found / already_expired)
 *      and, for matched rows, the original trade details
 *   4. Commit — all-or-nothing: every row must be "matched" (Booked state)
 *
 * Pattern mirrors CommodityTradeBooking but without the enrichment / staged-import layer.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Group as PanelGroup, Panel } from 'react-resizable-panels';
import { useAuth } from '../../../components/auth/AuthContext';
import {
  tradeImportService,
  TradeImportConfig,
  ExpiryImportResult,
  ExpiryImportedTradeRow,
} from '../../../services/api/tradeImportService';
import { clientService } from '../../../services/clientService';
import CommodityTradesGrid from '../../../components/grids/CommodityTradesGrid';
import SystemDateDisplay from '../../../components/common/SystemDateDisplay';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import ResizeHandle from '../../../components/common/ResizeHandle';
import Toast from '../../../components/common/Toast';
import { useSystemDate } from '../../../hooks/useSystemDate';
import './CommodityExpiryUpload.css';

type PageStep = 'idle' | 'selecting' | 'uploading' | 'previewing' | 'committing';

const MODULE = 'Commodities';

const LOOKUP_FIELD_LABEL_KEYS: Record<string, string> = {
  LegacyReference1: 'commodities.expiryImport.lookupFieldLegacyRef1',
  LegacyReference2: 'commodities.expiryImport.lookupFieldLegacyRef2',
  TradeNumber: 'commodities.expiryImport.lookupFieldTradeNumber',
};

/** Format YYYY-MM-DD → DD/MM/YYYY */
function fmtDate(v: any): string {
  if (typeof v !== 'string' || !v) return v ?? '';
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return v;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function fmtNum(v: any, decimals = 2): string {
  const n = parseFloat(String(v));
  if (isNaN(n)) return v ?? '';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

const CommodityExpiryUpload: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || user?.id || '';
  const systemDateResult = useSystemDate(clientId);

  // ── Bottom grid state ──
  const [trades, setTrades] = useState<any[]>([]);
  const [tradesLoading, setTradesLoading] = useState(true);

  // ── Date range filter — defaults to current calendar month ──
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const monthEndStr = `${monthEnd.getFullYear()}-${String(monthEnd.getMonth() + 1).padStart(2, '0')}-${String(monthEnd.getDate()).padStart(2, '0')}`;
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(monthEndStr);

  // ── Configs ──
  const [configs, setConfigs] = useState<TradeImportConfig[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState('');

  // ── File ──
  const [fileBase64, setFileBase64] = useState('');
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Step ──
  const [step, setStep] = useState<PageStep>('idle');

  // ── Preview result ──
  const [previewResult, setPreviewResult] = useState<ExpiryImportResult | null>(null);

  // ── Toast ──
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning'; visible: boolean }>({
    message: '',
    type: 'success',
    visible: false,
  });

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setToast({ message, type, visible: true });
  }, []);

  // ── Load expiry configs on mount ──
  useEffect(() => {
    if (!clientId) return;
    tradeImportService.listConfigs(clientId, MODULE, 'Expiry')
      .then(setConfigs)
      .catch(() => showToast(t('commodities.expiryImport.loadConfigsError'), 'error'));
  }, [clientId, t, showToast]);

  // ── Load commodity trades for bottom grid ──
  const loadTrades = useCallback(async () => {
    if (!clientId) return;
    try {
      setTradesLoading(true);
      const data = await clientService.getCommodityTrades(clientId, dateFrom || undefined, dateTo || undefined);
      setTrades(data);
    } catch (err) {
      console.error('Error loading commodity trades:', err);
    } finally {
      setTradesLoading(false);
    }
  }, [clientId, dateFrom, dateTo]);

  useEffect(() => {
    loadTrades();
  }, [loadTrades]);

  // ── Selected config ──
  const selectedConfig = useMemo(
    () => configs.find((c) => c.id === selectedConfigId) || null,
    [configs, selectedConfigId]
  );

  // ── File upload handler ──
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result as string;
      // Strip "data:...;base64," prefix
      const base64 = result.split(',')[1] || result;
      setFileBase64(base64);
      setFileName(file.name);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  const handleClearFile = useCallback(() => {
    setFileBase64('');
    setFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // ── Start import flow ──
  const handleStartImport = () => setStep('selecting');

  const handleCancelImport = () => {
    setStep('idle');
    setFileBase64('');
    setFileName('');
    setSelectedConfigId('');
    setPreviewResult(null);
  };

  // ── Preview ──
  const handlePreview = useCallback(async () => {
    if (!selectedConfigId || !fileBase64 || !fileName) return;
    setStep('uploading');
    try {
      const result = await tradeImportService.importExpiry(clientId, MODULE, {
        configId: selectedConfigId,
        fileBase64,
        fileName,
        mode: 'preview',
      });
      setPreviewResult(result);
      setStep('previewing');
    } catch (err: any) {
      console.error('Expiry preview failed:', err);
      showToast(err.message || t('commodities.expiryImport.previewError'), 'error');
      setStep('selecting');
    }
  }, [clientId, selectedConfigId, fileBase64, fileName, t, showToast]);

  // ── Commit ──
  const handleCommit = useCallback(async () => {
    if (!selectedConfigId || !fileBase64 || !fileName) return;

    setStep('committing');
    try {
      const result = await tradeImportService.importExpiry(clientId, MODULE, {
        configId: selectedConfigId,
        fileBase64,
        fileName,
        mode: 'commit',
      });

      if (!result) throw new Error('No response');

      if (result.tradesUpdated > 0) {
        showToast(
          t('commodities.expiryImport.commitSuccess', { count: result.tradesUpdated }),
          'success'
        );
        handleCancelImport();
        loadTrades();
      } else {
        // Backend refused commit (rows not all matched)
        setPreviewResult(result);
        setStep('previewing');
        showToast(t('commodities.expiryImport.commitBlocked'), 'error');
      }
    } catch (err: any) {
      console.error('Expiry commit failed:', err);
      showToast(err.message || t('commodities.expiryImport.commitError'), 'error');
      setStep('previewing');
    }
  }, [clientId, selectedConfigId, fileBase64, fileName, t, showToast, handleCancelImport, loadTrades]);

  // ── Commit blocked? ──
  const commitBlocked = useMemo(() => {
    if (!previewResult) return true;
    return previewResult.notFoundRows > 0 || previewResult.alreadyExpiredRows > 0 || previewResult.errorRows > 0;
  }, [previewResult]);

  // ── Match status badge ──
  const MatchBadge: React.FC<{ status: ExpiryImportedTradeRow['matchStatus'] }> = ({ status }) => {
    const className = `expiry-match-badge expiry-match-badge--${status}`;
    const label = t(`commodities.expiryImport.matchStatus.${
      status === 'matched' ? 'matched'
      : status === 'not_found' ? 'notFound'
      : 'alreadyExpired'
    }`);
    return <span className={className}>{label}</span>;
  };

  return (
    <div className="expiry-upload-page">
      {/* Page header */}
      <div className="expiry-upload-header">
        <h2 className="expiry-upload-title">{t('commodities.expiryImport.title')}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <DateRangeFilter
            dateFrom={dateFrom}
            dateTo={dateTo}
            onApply={(from, to) => { setDateFrom(from); setDateTo(to); }}
            disabled={tradesLoading}
          />
          <SystemDateDisplay clientId={clientId} systemDateData={systemDateResult} />
        </div>
      </div>

      <PanelGroup orientation="vertical" className="expiry-panel-group">
        {/* ── Top Panel: Import workflow ── */}
        <Panel defaultSize={45} minSize={20}>
        <div className="expiry-upload-content">

        {/* Idle */}
        {step === 'idle' && (
          <div className="commodity-import-empty">
            <p>{t('commodities.expiryImport.importEmpty')}</p>
            <button className="import-btn import-btn-primary" onClick={handleStartImport}>
              {t('commodities.expiryImport.importButton')}
            </button>
          </div>
        )}

        {/* Uploading / committing overlay */}
        {(step === 'uploading' || step === 'committing') && (
          <div className="expiry-loading-overlay">
            <div className="expiry-spinner" />
            <p>{step === 'uploading'
              ? t('commodities.expiryImport.processingFile', { fileName })
              : t('commodities.expiryImport.committing')}
            </p>
            <p className="expiry-loading-hint">{t('commodities.expiryImport.processingPatience')}</p>
          </div>
        )}

        {/* Selecting config + file */}
        {step === 'selecting' && (
          <div className="commodity-import-form">

            {configs.length === 0 ? (
              <p className="expiry-no-configs">{t('commodities.expiryImport.noConfigs')}</p>
            ) : (
              <>
                {/* Config selector */}
                <div className="commodity-import-field">
                  <label className="commodity-import-label">
                    {t('commodities.tradeBooking.configLabel')}
                  </label>
                  <select
                    value={selectedConfigId}
                    onChange={(e) => setSelectedConfigId(e.target.value)}
                  >
                    <option value="">{t('commodities.expiryImport.selectConfig')}</option>
                    {configs.map((c) => (
                      <option key={c.id} value={c.id || ''}>{c.name}</option>
                    ))}
                  </select>
                </div>

                {/* File dropzone — visible once config is selected */}
                {selectedConfigId && (
                  <div className="commodity-import-field">
                    <label className="commodity-import-label">
                      {t('commodities.expiryImport.fileLabel')}
                    </label>
                    <div
                      className="commodity-file-dropzone"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFileChange}
                      />
                      {fileName ? (
                        <span className="commodity-file-name">{fileName}</span>
                      ) : (
                        <span>{t('commodities.expiryImport.dropzoneText')}</span>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Action buttons */}
            <div className="commodity-import-actions">
              <button
                className="import-btn import-btn-primary"
                disabled={!selectedConfigId || !fileBase64}
                onClick={handlePreview}
              >
                {t('commodities.expiryImport.preview')}
              </button>
              <button className="import-btn import-btn-secondary" onClick={handleCancelImport}>
                {t('commodities.tradeBooking.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Previewing */}
        {step === 'previewing' && previewResult && (
          <div className="expiry-preview-panel">
            {/* Header — title left, action buttons right */}
            <div className="commodity-preview-header">
              <div>
                <span className="commodity-preview-title">{t('commodities.expiryImport.preview')}</span>
                {fileName && (
                  <span className="commodity-preview-subtitle">{fileName}</span>
                )}
              </div>
              <div className="commodity-preview-actions">
                <button
                  className="import-btn import-btn-primary"
                  disabled={commitBlocked}
                  title={commitBlocked ? t('commodities.expiryImport.errorsPreventCommit') : ''}
                  onClick={handleCommit}
                >
                  {t('commodities.expiryImport.importButton')} ({previewResult.matchedRows})
                </button>
                <button className="import-btn import-btn-secondary" onClick={() => setStep('selecting')}>
                  {t('commodities.expiryImport.back')}
                </button>
                <button className="import-btn import-btn-secondary" onClick={handleCancelImport}>
                  {t('commodities.tradeBooking.cancel')}
                </button>
              </div>
            </div>

            {/* Summary bar */}
            <div className="expiry-preview-summary">
              <span className="expiry-summary-item expiry-summary-matched">
                {t('commodities.expiryImport.matchStatus.matched')}: {previewResult.matchedRows}
              </span>
              <span className="expiry-summary-item expiry-summary-notfound">
                {t('commodities.expiryImport.matchStatus.notFound')}: {previewResult.notFoundRows}
              </span>
              <span className="expiry-summary-item expiry-summary-expired">
                {t('commodities.expiryImport.matchStatus.alreadyExpired')}: {previewResult.alreadyExpiredRows}
              </span>
              {previewResult.errorRows > 0 && (
                <span className="expiry-summary-item expiry-summary-errors">
                  {t('commodities.expiryImport.matchStatus.errors')}: {previewResult.errorRows}
                </span>
              )}
            </div>

            {commitBlocked && (
              <p className="expiry-commit-blocked">{t('commodities.expiryImport.commitBlocked')}</p>
            )}

            {/* Preview table */}
            <div className="commodity-preview-table-wrapper">
              <table className="commodity-preview-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('commodities.positions.grid.workflowState')}</th>
                    {/* Group A — from file */}
                    <th>{t(LOOKUP_FIELD_LABEL_KEYS[selectedConfig?.lookupField || ''] ?? 'commodities.expiryImport.lookupFieldLegacyRef1')}</th>
                    <th>{t('commodities.fields.settlementPrice')}</th>
                    <th>{t('commodities.fields.settlementAmount')}</th>
                    <th>{t('commodities.fields.paymentDate')}</th>
                    {/* Group B — matched trade */}
                    <th>{t('commodities.positions.grid.tradeNumber')}</th>
                    <th>{t('commodities.positions.grid.underlyingName')}</th>
                    <th>{t('commodities.fields.direction')}</th>
                    <th>{t('commodities.fields.lots')}</th>
                    <th>{t('commodities.fields.price')}</th>
                    <th>{t('commodities.fields.maturityDate')}</th>
                    <th>{t('commodities.fields.counterpartyName')}</th>
                  </tr>
                </thead>
                <tbody>
                  {previewResult.rows.map((row) => {
                    const lookupKey = selectedConfig?.lookupField || 'LegacyReference1';
                    const mt = row.matchedTrade;
                    const rowClass = row.matchStatus === 'matched' ? 'row-valid'
                      : row.matchStatus === 'already_expired' ? 'row-warning'
                      : 'row-error';
                    return (
                      <tr
                        key={row.rowIndex}
                        className={rowClass}
                      >
                        <td className="row-status-cell">{row.rowIndex}</td>
                        <td><MatchBadge status={row.matchStatus} /></td>
                        {/* Group A */}
                        <td>{row.fields[lookupKey] ?? ''}</td>
                        <td>{row.fields.SettlementPrice != null ? fmtNum(row.fields.SettlementPrice, 4) : ''}</td>
                        <td>{row.fields.SettlementAmount != null ? fmtNum(row.fields.SettlementAmount, 2) : ''}</td>
                        <td>{fmtDate(row.fields.PaymentDate)}</td>
                        {/* Group B */}
                        <td>{mt?.tradeNumber || ''}</td>
                        <td>{mt?.underlyingName || ''}</td>
                        <td>{mt?.direction || ''}</td>
                        <td>{mt != null ? fmtNum(mt.lots, 0) : ''}</td>
                        <td>{mt != null ? fmtNum(mt.price, 4) : ''}</td>
                        <td>{fmtDate(mt?.maturityDate)}</td>
                        <td>{mt?.counterpartyName || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

          </div>
        )}

        </div>
        </Panel>

        <ResizeHandle direction="vertical" />

        {/* ── Bottom Panel: Trades Grid ── */}
        <Panel defaultSize={55} minSize={15}>
          <div className="expiry-grid-panel">
            <div className="commodity-panel-header">
              <div className="commodity-panel-title">{t('commodities.tradeBooking.existingTrades')}</div>
            </div>
            <CommodityTradesGrid trades={trades} loading={tradesLoading} />
          </div>
        </Panel>
      </PanelGroup>

      {/* Toast */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.visible}
        onClose={() => setToast((prev) => ({ ...prev, visible: false }))}
      />
    </div>
  );
};

export default CommodityExpiryUpload;
